<template>
    <BaseModal :title="$t('title')" v-on="$listeners">
        <BaseCard padded class="l-stack l-gap-2">
            <div>
                <p class="form-label">
                    {{ $t('truck') }}
                </p>

                <BaseMultiselect
                    v-model="truck"
                    :options="registerableTrucks"
                    label="name"
                    :loading="isAllTrucksLoading"
                    :placeholder="$t('truck')"
                />
            </div>

            <div>
                <p class="form-label">
                    {{ $t('vin') }}
                </p>

                <BaseInput v-model="vin" :placeholder="$t('vin')" />
            </div>

            <div>
                <p class="form-label">
                    {{ $t('startDate') }}
                </p>

                <DateInput
                    v-model="startDate"
                    :placeholder="$t('startDate')"
                    :disabled-dates="pastDates"
                />
            </div>

            <div>
                <p class="form-label">
                    {{ $t('installationDate') }}
                </p>

                <DateInput
                    v-model="installationDate"
                    :placeholder="$t('installationDate')"
                    :disabled-dates="futureDates"
                    :clear-button="true"
                />
            </div>
        </BaseCard>

        <template #footer>
            <div class="l-inline l-justify-end">
                <BaseButton
                    size="small"
                    :disabled="isDisabled"
                    :is-loading="isLoading"
                    @click="handleSubmit"
                >
                    {{ $t('submit') }}
                </BaseButton>
            </div>
        </template>
    </BaseModal>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import moment from 'moment'

import { httpHelper } from '@/utils'
import BaseButton from '@/components/redesigned/BaseButton'
import BaseCard from '@/components/redesigned/BaseCard'
import BaseInput from '@/components/redesigned/BaseInput'
import BaseModal from '@/components/redesigned/BaseModal'
import BaseMultiselect from '@/components/redesigned/BaseMultiselect'
import DateInput from '@/components/redesigned/DateInput'

export default {
    name: 'LsvaTruckRegisterModal',
    components: {
        BaseButton,
        BaseCard,
        BaseInput,
        BaseModal,
        BaseMultiselect,
        DateInput,
    },
    props: {
        onSubmit: {
            type: Function,
            default: null,
        },
    },
    data() {
        return {
            isLoading: false,
            truck: null,
            vin: '',
            startDate: new Date(),
            installationDate: null,
        }
    },
    computed: {
        ...mapState('lsva', ['isAllTrucksLoading']),
        ...mapGetters('lsva', ['registerableTrucks']),
        isDisabled() {
            const now = moment()
            return (
                !this.truck ||
                !this.startDate ||
                (this.startDate &&
                    moment(this.startDate).isBefore(now, 'day')) ||
                (this.installationDate &&
                    moment(this.installationDate).isSameOrAfter(now, 'day'))
            )
        },
        futureDates() {
            return {
                from: moment()
                    .startOf('day')
                    .toDate(),
            }
        },
        pastDates() {
            return {
                to: moment()
                    .subtract(1, 'day')
                    .endOf('day')
                    .toDate(),
            }
        },
    },
    watch: {
        truck(newTruck) {
            this.vin = newTruck?.vin || ''
            // this.startDate = newTruck?.start_date || null
            this.installationDate = newTruck?.installation_date || null
        },
    },
    mounted() {
        this.fetchAllTrucks()
    },
    methods: {
        ...mapActions('lsva', ['fetchAllTrucks']),
        async handleSubmit() {
            if (!this.truck) {
                return false
            }

            this.isLoading = true
            const errorTitle = this.$t('shared.error')
            const successTitle = this.$t('shared.success')

            const body = {
                vin: this.vin,
                start_date: this.startDate
                    ? moment(this.startDate).format('YYYY-MM-DD')
                    : null,
            }

            if (this.installationDate) {
                body.installation_date = moment(this.installationDate).format(
                    'YYYY-MM-DD'
                )
            }

            try {
                const response = await httpHelper.post(
                    `lsva/truck-registration/${this.truck.id}/`,
                    body
                )
                this.onSubmit?.()
                this.$notify({
                    title: successTitle,
                    text: response.data,
                })
                this.$emit('close')
            } catch (e) {
                this.$notify({
                    title: errorTitle,
                    text: e.response?.data,
                })
            } finally {
                this.isLoading = false
            }
        },
    },
}
</script>

<i18n>
{
    "en": {
        "installationDate": "Installation date",
        "startDate": "Start date",
        "submit": "Register",
        "title": "Register truck",
        "truck": "Truck",
        "vin": "VIN"
    },
    "de": {
        "installationDate": "Installationsdatum",
        "startDate": "Startdatum",
        "submit": "Registrieren",
        "title": "Lkw registrieren",
        "truck": "Lkw",
        "vin": "VIN"
    },
    "fr": {
        "installationDate": "Date d'installation",
        "startDate": "Date de début",
        "submit": "Enregistrer",
        "title": "Enregistrer le camion",
        "truck": "Camion",
        "vin": "VIN"
    },
    "it": {
        "installationDate": "Data di installazione",
        "startDate": "Data di inizio",
        "submit": "Registrare",
        "title": "Registrare il camion",
        "truck": "Camion",
        "vin": "VIN"
    }
}
</i18n>
